import { useState, useEffect, useCallback } from "react";
import { extractTitleFromPRD } from "../utils/prdUtils";

/**
 * Custom hook for managing sessions and their titles
 * @param {string} backendUrl - The backend API URL
 * @returns {Object} - Sessions state and handlers
 */
export const useSessions = (backendUrl) => {
  const [sessionIds, setSessionIds] = useState([]);
  const [sessionTitles, setSessionTitles] = useState({});
  const [sessionUserEmails, setSessionUserEmails] = useState({});
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isStartingSession, setIsStartingSession] = useState(false);

  // Fetch available sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        // Get user_id from localStorage
        const userId = localStorage.getItem("userId");
        const url = userId 
          ? `${backendUrl}/sessions?user_id=${userId}`
          : `${backendUrl}/sessions`;
        
        const res = await fetch(url);
        if (res.ok) {
          const sessions = await res.json();
          // Handle both old format (array of strings) and new format (array of objects)
          const ids = sessions.map(s => typeof s === 'string' ? s : s.session_id);
          setSessionIds(ids);

          // Extract user emails for admin
          const emailMap = {};
          sessions.forEach(s => {
            if (typeof s === 'object' && s.session_id && s.user_email) {
              emailMap[s.session_id] = s.user_email;
            }
          });
          setSessionUserEmails(emailMap);

          // Fetch titles for each session
          const titlePromises = ids.map(async (sid) => {
            try {
              const historyUrl = userId
                ? `${backendUrl}/sessions/${sid}/history?user_id=${userId}`
                : `${backendUrl}/sessions/${sid}/history`;
              const historyRes = await fetch(historyUrl);
              if (historyRes.ok) {
                const data = await historyRes.json();
                if (data.prd) {
                  const title = extractTitleFromPRD(data.prd);
                  if (title) {
                    return { sid, title };
                  }
                }
              }
            } catch (e) {
              console.error(`Failed to load title for session ${sid}`, e);
            }
            return null;
          });

          const titles = await Promise.all(titlePromises);
          const titleMap = {};
          titles.forEach((item) => {
            if (item) {
              titleMap[item.sid] = item.title;
            }
          });
          setSessionTitles(titleMap);
        }
      } catch (e) {
        console.error("Failed to load sessions", e);
      }
    };
    fetchSessions();
  }, [backendUrl]);

  // Handler to start a new chat session - memoized to prevent unnecessary re-renders
  const handleStartChatSession = useCallback(async () => {
    setIsStartingSession(true);
    try {
      // Get user_id from localStorage
      const userId = localStorage.getItem("userId");
      const requestBody = {
        prd: "Create a modern web application with a clean, responsive design. Include interactive elements and a professional user interface.",
      };
      
      // Add user_id if available
      if (userId) {
        requestBody.user_id = parseInt(userId, 10);
      }
      
      const startResponse = await fetch(`${backendUrl}/start_session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!startResponse.ok) {
        throw new Error(`HTTP error! status: ${startResponse.status}`);
      }

      const sessionData = await startResponse.json();
      const sessionId = sessionData.session_id;

      setCurrentSessionId(sessionId);
      setSessionIds((prev) => [
        sessionId,
        ...prev.filter((id) => id !== sessionId),
      ]);
    } catch (error) {
      console.error("Error starting chat session:", error);
      alert("Failed to start chat session. Please try again.");
    } finally {
      setIsStartingSession(false);
    }
  }, [backendUrl]);

  // Handler to update session title - memoized to prevent unnecessary re-renders
  const updateSessionTitle = useCallback((sessionId, title) => {
    setSessionTitles((prev) => ({ ...prev, [sessionId]: title }));
  }, []);

  return {
    sessionIds,
    sessionTitles,
    sessionUserEmails,
    currentSessionId,
    setCurrentSessionId,
    isStartingSession,
    handleStartChatSession,
    updateSessionTitle,
  };
};
